import React, { useState } from "react";
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Grid,
  Popover,
  Badge,
} from "@mui/material"; 
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom"; // Use navigate hook for navigation

// Styled Badge for Cart Icon
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 5,
    top: 30,
    color: "rgb(233, 61, 103)",
    fontWeight: 600,
    background: "white",
    border: `2px solid white`,
    padding: "0 4px",
  },
}));

// Cart Item Component
const CartItem = ({ item, index, onRemove, currency }) => (
  <Grid item xs={12} key={item.id}>
    <Box display="flex" alignItems="center">
      <Box mr={2}>
        <img
          src={item.image}
          style={{
            width: 50,
            height: 50,
            objectFit: "cover",
            borderRadius: "4px",
          }}
        />
      </Box>
      <Box flexGrow={1}>
        <Typography variant="body1">
          {item.quantity} x {item.name} @ {currency} {item.salePrice.toFixed(2)}{" "}
          each
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total: {currency} {(item.quantity * item.salePrice).toFixed(2)}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => onRemove(index)}
      >
        Remove
      </Button>
    </Box>
  </Grid>
);

const CartHeader = ({ cart, setCart, checkout, setCheckOut, currency }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const navigate = useNavigate(); // Use the navigate function to handle page transitions

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveFromCart = (index) => {
    const updatedCart = [...cart];
    updatedCart.splice(index, 1);
    setCart(updatedCart);
  };

  const handleCartOpen = () => setPopoverOpen(true);
  const handleCartClose = () => setPopoverOpen(false);

  const handleCheckout = () => {
    setPopoverOpen(false);
    setCheckOut(true);
  };

  return (
    <Grid container spacing={3}>
      {/* Order Title */}
      <Grid item xs={10.5}>
        <Typography variant="subtitle2" gutterBottom>
          Order
        </Typography>
      </Grid>

      {/* Cart Section */}
      <Grid item xs={0.5}>
        <StyledBadge
          badgeContent={cart.length}
          color="secondary"
          onClick={handleCartOpen}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "rgb(233, 61, 103)",
              color: "white",
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 25 }} />
          </Avatar>
        </StyledBadge>
        <Popover
          open={popoverOpen}
          onClose={handleCartClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{ marginTop: 6 }}
        >
          <Box sx={{ width: 450, p: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Cart
            </Typography>
            {cart.length === 0 ? (
              <Typography variant="body1" color="text.secondary" mt={2}>
                Your cart is empty.
              </Typography>
            ) : (
              <Grid container spacing={2} mt={2}>
                {cart.map((item, index) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    index={index}
                    onRemove={handleRemoveFromCart}
                    currency={currency}
                  />
                ))}
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight="bold" textAlign="right">
                    Total: {currency}
                    {cart
                      .reduce(
                        (total, item) => total + item.quantity * item.salePrice,
                        0,
                      )
                      .toFixed(2)}
                  </Typography>
                </Grid>
                {/* Checkout Button */}
                <Grid item xs={12} mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleCheckout}
                  >
                    Proceed to Checkout
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Popover>
      </Grid>

      {/* Profile Section */}
      <Grid item xs={1}>
        <Box display="flex" alignItems="center">
          <Button
            onClick={handleProfileClick}
            sx={{
              display: "flex",
              alignItems: "center",
              textTransform: "none",
              padding: 0,
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                marginRight: 1,
                bgcolor: "rgb(233, 61, 103)",
                color: "white",
              }}
            >
              <PersonIcon />
            </Avatar>
            <Typography variant="subtitle2">John Doe</Typography>
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem>Profile</MenuItem>
            <MenuItem>Settings</MenuItem>
            <Divider />
            <MenuItem onClick={handleProfileClose} sx={{ color: "error.main" }}>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CartHeader;
